import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaCrosshairs, FaLaptopCode, FaCloud, FaClipboardCheck, FaShieldVirus,
  FaBug, FaGlobe, FaMobileAlt, FaCogs, FaUserShield,
  FaProjectDiagram, FaNetworkWired, FaEnvelope, FaArrowRight
} from 'react-icons/fa';
import { motion } from 'motion/react';
import ScrollReveal from './ScrollReveal';
import PageNavbar from './PageNavbar';
import {
  defaultViewport,
  staggerContainer,
  staggerItem,
  getRevealProps,
} from '../motion/scrollMotion';
import Footer from './Footer';
import './CybersecurityPage.css';

const pillars = [
  {
    icon: <FaCrosshairs />,
    title: 'Offensive Security',
    desc: 'Penetration testing, red teaming, and adversary simulation to identify and exploit vulnerabilities before attackers do.',
  },
  {
    icon: <FaLaptopCode />,
    title: 'Application Security',
    desc: 'Web and mobile application security testing, secure code review, and SDLC integration for modern applications.',
  },
  {
    icon: <FaCloud />,
    title: 'Cloud Security',
    desc: 'Multi-cloud security assessment, configuration review, and cloud architecture hardening for AWS, Azure, and GCP.',
  },
  {
    icon: <FaClipboardCheck />,
    title: 'Compliance & Governance',
    desc: 'ISO 27001, NIST CSF 2.0, CIS Controls, NIST AI RMF, Cyber Essentials Plus, UK GDPR, and NCSC CAF compliance assessments, gap analysis, internal audit, and certification support.',
  },
  {
    icon: <FaShieldVirus />,
    title: 'Security Monitoring, Detection & Response',
    desc: '24/7 continuous SIEM monitoring, threat detection, alert triage, and incident response powered by our expert SOC analysts.',
  },
];

// Curated cybersecurity portfolio — one card per distinct service page (no two
// cards point to the same page). Penetration testing and vulnerability
// assessment are one engagement (VAPT); the SOC monitoring models are presented
// as a single "Security Monitoring & SOC" card. Managed/co-managed variants and
// the individual compliance frameworks live on their own pages via the menu.
const services = [
  { icon: <FaBug />, title: 'Penetration Testing & Vulnerability Assessment', href: '/services/vapt', desc: 'Combined vulnerability assessment and expert-led penetration testing across network, application, and cloud — validating real, exploitable risk. Delivered by CISSP, CEH, and OSCP certified testers.' },
  { icon: <FaGlobe />, title: 'Web Application Security Testing', href: '/services/web-application-penetration-testing', desc: 'OWASP Top 10 testing, business logic analysis, authentication review, and API security assessment for web applications.' },
  { icon: <FaMobileAlt />, title: 'Mobile Application Security Testing', href: '/services/mobile-app-penetration-testing', desc: 'iOS and Android security testing following OWASP MASTG — data storage, cryptography, and backend API review.' },
  { icon: <FaNetworkWired />, title: 'Network Security', href: '/services/network-security', desc: 'Design and harden a defensible network — firewalls and perimeter, segmentation, IDS/IPS, and NAC (our own or enterprise), with secure access and monitoring.' },
  { icon: <FaEnvelope />, title: 'Email Security', href: '/services/email-security-audit', desc: 'Secure email gateway implementation (enterprise or our own), DMARC enforcement, and continuous management and monitoring against phishing and BEC.' },
  { icon: <FaCloud />, title: 'Cloud Security', href: '/services/cloud-security-audit', desc: 'Multi-cloud assessments for AWS, Azure, and GCP — IAM, storage, serverless, containers, and IaC misconfiguration review.' },
  { icon: <FaProjectDiagram />, title: 'Zero Trust & Security Architecture', href: '/services/zero-trust-architecture', desc: 'Zero trust maturity, IAM modernization, micro-segmentation, conditional access, and continuous monitoring design.' },
  { icon: <FaShieldVirus />, title: 'Security Monitoring & 24/7 SOC', href: '/services/security-operations-centre', desc: '24/7 SIEM monitoring, threat detection, and analyst-led triage from our SOC — available fully managed or co-managed alongside your team.' },
  { icon: <FaCrosshairs />, title: 'Incident Response', href: '/services/incident-response', desc: 'Rapid breach containment, digital forensics, ransomware response, and incident response planning.' },
  { icon: <FaClipboardCheck />, title: 'Compliance & Security Audits', href: '/services/category/compliance-governance', desc: 'ISO 27001, NIST CSF 2.0, CIS Controls, NIST AI RMF, Cyber Essentials Plus, and UK GDPR assessments, internal audit, and certification support.' },
  { icon: <FaUserShield />, title: 'vCISO Services', href: '/services/vciso-services', desc: 'Ongoing, embedded security leadership — a virtual CISO who runs your GRC program, risk, and board reporting without a full-time hire.' },
  { icon: <FaCogs />, title: 'Security Consulting', href: '/services/cyber-security-strategic-consulting', desc: 'Point-in-time assess & roadmap — maturity assessment against your chosen framework, target-state design, and a prioritized security roadmap.' },
];

const methodology = [
  { step: '01', name: 'Scoping', desc: 'Define engagement boundaries, target assets, and testing rules' },
  { step: '02', name: 'Reconnaissance', desc: 'Gather intelligence on the target environment and surface attack paths' },
  { step: '03', name: 'Exploitation', desc: 'Simulate real-world attacks to validate exploitable vulnerabilities' },
  { step: '04', name: 'Post-Exploitation', desc: 'Demonstrate business impact, lateral movement, and data exfiltration risk' },
  { step: '05', name: 'Reporting', desc: 'Deliver risk-prioritized findings with actionable remediation guidance' },
  { step: '06', name: 'Retest', desc: 'Validate that remediations have closed identified vulnerabilities' },
];

const CybersecurityPage = () => {
  return (
    <div className="cyber-page">
      <PageNavbar />

      {/* Hero */}
      <section className="cyber-hero">
        <div className="cyber-hero-background"></div>
        <div className="cyber-hero-stars"></div>
        <ScrollReveal className="cyber-hero-content" animateOnMount variant="fadeUp" duration={1.05}>
          <span className="cyber-badge">Cybersecurity Services</span>
          <h1 className="cyber-hero-title">
            Proactive Cybersecurity Services<br />
            for <span className="cyber-accent">Modern Organizations.</span>
          </h1>
          <p className="cyber-hero-description">
            Identify vulnerabilities, strengthen defenses, and improve resilience with cybersecurity
            services delivered by CISSP, CEH, OSCP, and CISA certified security professionals.
          </p>
        </ScrollReveal>
      </section>

      {/* Five Pillars of Cyber Defense */}
      <section className="cyber-pillars-section">
        <div className="cyber-container">
          <motion.div className="cyber-section-header" {...getRevealProps('fadeUp')}>
            <span className="cyber-block-tag">Five Pillars of Cyber Defense</span>
            <h2 className="cyber-section-title">A Complete Defense Framework</h2>
            <p className="cyber-section-subtitle">
              Five interconnected disciplines that cover every stage of the cybersecurity lifecycle —
              from offensive testing to continuous monitoring and incident response.
            </p>
          </motion.div>

          <motion.div
            className="cyber-pillars-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={staggerContainer}
          >
            {pillars.map((p, i) => (
              <motion.div className="cyber-pillar-card" key={i} variants={staggerItem}>
                <div className="cyber-pillar-icon">{p.icon}</div>
                <h3 className="cyber-pillar-title">{p.title}</h3>
                <p className="cyber-pillar-desc">{p.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Full Service Portfolio */}
      <section className="cyber-services-section">
        <div className="cyber-container">
          <motion.div className="cyber-section-header light" {...getRevealProps('fadeUp')}>
            <span className="cyber-block-tag">Full Service Portfolio</span>
            <h2 className="cyber-section-title light">Every Discipline. One Trusted Partner.</h2>
            <p className="cyber-section-subtitle light">
              A complete cybersecurity portfolio — from offensive testing and cloud assessments to
              24/7 monitoring, incident response, and strategic consulting.
            </p>
          </motion.div>

          <motion.div
            className="cyber-services-grid"
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={{
              ...staggerContainer,
              visible: { transition: { staggerChildren: 0.08, delayChildren: 0.06 } },
            }}
          >
            {services.map((s, i) => (
              <motion.div className="cyber-service-card" key={i} variants={staggerItem}>
                <div className="cyber-service-icon">{s.icon}</div>
                <h3 className="cyber-service-title">{s.title}</h3>
                <p className="cyber-service-desc">{s.desc}</p>
                <Link to={s.href} className="cyber-service-link">
                  Learn more <FaArrowRight />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="cyber-why-section">
        <div className="cyber-container">
          <div className="cyber-why-grid">
            <motion.div className="cyber-why-text" {...getRevealProps('slideLeft')}>
              <span className="cyber-block-tag">Why It Matters</span>
              <h2 className="cyber-section-title">Proactive Security for Modern Threats.</h2>
              <p className="cyber-block-body">
                Organizations face increasingly sophisticated cyber threats. Regular cybersecurity
                engagements validate your security controls, identify gaps before they are exploited,
                and provide evidence of due diligence for regulatory compliance.
              </p>
              <ul className="cyber-bullet-list">
                <li>Validate security controls and defense mechanisms</li>
                <li>Meet compliance requirements (ISO 27001, NIST CSF, CIS Controls)</li>
                <li>Identify attack paths that automated tools miss</li>
                <li>Reduce the risk of data breaches and financial loss</li>
                <li>Build confidence in your security posture</li>
              </ul>
            </motion.div>

            <motion.div className="cyber-methodology-card" {...getRevealProps('slideRight', 0.12)}>
              <span className="cyber-block-tag">Testing Methodology</span>
              <h3 className="cyber-methodology-title">A Repeatable Six-Stage Process</h3>
              <div className="cyber-methodology-steps">
                {methodology.map((m, i) => (
                  <div className="cyber-methodology-step" key={i}>
                    <div className="cyber-methodology-num">{m.step}</div>
                    <div className="cyber-methodology-content">
                      <div className="cyber-methodology-name">{m.name}</div>
                      <div className="cyber-methodology-desc">{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cyber-cta-section">
        <div className="cyber-container">
          <motion.div className="cyber-cta-card" {...getRevealProps('scaleUp')}>
            <div className="cyber-cta-left">
              <h2 className="cyber-cta-title">Ready to strengthen your defenses?</h2>
              <p className="cyber-cta-desc">
                Talk to one of our certified security professionals about a tailored cybersecurity
                engagement — we respond to every inquiry within one business day.
              </p>
              <div className="cyber-cta-actions">
                <button className="cyber-cta-btn primary">Book Consultation <FaArrowRight /></button>
                <button className="cyber-cta-btn secondary">View All Services</button>
              </div>
            </div>
            <div className="cyber-cta-right">
              <div className="cyber-cta-stat">
                <div className="cyber-cta-stat-value">24/7</div>
                <div className="cyber-cta-stat-label">SOC Monitoring</div>
              </div>
              <div className="cyber-cta-stat">
                <div className="cyber-cta-stat-value">CISSP/CEH/OSCP</div>
                <div className="cyber-cta-stat-label">Certified Testers</div>
              </div>
              <div className="cyber-cta-stat">
                <div className="cyber-cta-stat-value">500+</div>
                <div className="cyber-cta-stat-label">Engagements Delivered</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CybersecurityPage;
